import { useEffect, useRef, useState } from 'react';
import { HERO } from '../content';
import { imgUrl, srcSet } from '../theme';
import { prefersReducedMotion } from '../hooks';
import { quadToMatrix3d } from '../lib/homography';
import { BriefingScreen, COMPACT_SIZE, SCREEN_SIZE } from './Screens';
import './Hero.css';

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const ramp = (a, b, v) => {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const mix = (a, b, t) => a + (b - a) * t;
const centroid = (pts) => [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];

function metarTime(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}Z`;
}

// Geometry for one viewport size: where the photo sits (object-fit: cover),
// where the product panel starts (on the tablet screen in the photo, or below
// the fold) and where it comes to rest, centred and flat.
function measure(stage, hero) {
  const vw = stage.clientWidth;
  const vh = stage.clientHeight;
  const { w: iw, h: ih, focus, screen, target } = hero;
  const k = Math.max(vw / iw, vh / ih);
  const W = iw * k;
  const H = ih * k;
  const L = (vw - W) * focus[0];
  const T = (vh - H) * focus[1];

  const compact = vw < 760 || vw / vh < 0.95;
  const panel = compact ? COMPACT_SIZE : SCREEN_SIZE;
  const aspect = panel.w / panel.h;
  let fw = compact ? Math.min(vw - 40, panel.w) : Math.min(vw * 0.84, 1200);
  let fh = fw / aspect;
  const maxH = vh - (compact ? 110 : 150);
  if (fh > maxH) {
    fh = maxH;
    fw = fh * aspect;
  }
  const fx = (vw - fw) / 2;
  const fy = (vh - fh) / 2 + (compact ? 24 : 30);
  const rest = [
    [fx, fy],
    [fx + fw, fy],
    [fx, fy + fh],
    [fx + fw, fy + fh],
  ];

  const aligned = Boolean(screen) && !compact;
  let start;
  let origin;
  let zoom;
  let shift = [0, 0];
  if (aligned) {
    start = screen.map(([x, y]) => [L + x * k, T + y * k]);
    origin = centroid(start);
    zoom = fw / ((start[1][0] - start[0][0] + start[3][0] - start[2][0]) / 2);
    shift = [vw / 2 - origin[0], fy + fh / 2 - origin[1]];
  } else {
    const t = target || (screen ? centroid(screen).map((v, i) => v / (i ? ih : iw)) : focus);
    origin = [L + t[0] * W, T + t[1] * H];
    zoom = compact ? 1.3 : 1.4;
    const drop = vh * 0.55;
    const inset = fw * 0.07;
    start = [
      [fx + inset, fy + drop],
      [fx + fw - inset, fy + drop],
      [fx, fy + fh + drop],
      [fx + fw, fy + fh + drop],
    ];
  }

  return { vh, W, H, L, T, compact, aligned, panel, rest, start, origin, zoom, shift };
}

function render(g, t, el) {
  const cam = ramp(0, 1, t);
  const s = mix(1, g.zoom, cam);
  const dx = g.shift[0] * cam;
  const dy = g.shift[1] * cam;
  el.img.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${s})`;

  // The panel rides on the photographed screen, then squares up to face us.
  const flat = g.aligned ? ramp(0.42, 1, t) : ramp(0.1, 0.9, t);
  const quad = g.start.map((p, i) => {
    const q = g.aligned ? [g.origin[0] + (p[0] - g.origin[0]) * s + dx, g.origin[1] + (p[1] - g.origin[1]) * s + dy] : p;
    return [mix(q[0], g.rest[i][0], flat), mix(q[1], g.rest[i][1], flat)];
  });
  el.panel.style.transform = quadToMatrix3d(g.panel.w, g.panel.h, quad);
  el.panel.style.opacity = g.aligned ? ramp(0.1, 0.36, t) : ramp(0.08, 0.5, t);

  const out = ramp(0, 0.28, t);
  el.content.style.opacity = 1 - out;
  el.content.style.transform = `translate3d(0, ${-56 * out}px, 0)`;
  el.content.style.filter = out > 0.01 ? `blur(${6 * out}px)` : 'none';
  el.content.style.pointerEvents = out > 0.5 ? 'none' : '';
  el.pill.style.opacity = 1 - ramp(0, 0.1, t);
  el.shade.style.opacity = ramp(0.04, 0.95, t) * 0.88;
  el.fade.style.opacity = ramp(0.5, 1, t);
}

export default function Hero({ theme, onStart }) {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const shadeRef = useRef(null);
  const fadeRef = useRef(null);
  const panelRef = useRef(null);
  const contentRef = useRef(null);
  const pillRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [still, setStill] = useState(prefersReducedMotion);
  const [email, setEmail] = useState('');
  const [stamp, setStamp] = useState(metarTime);
  const { hero } = theme;

  useEffect(() => {
    const id = setInterval(() => setStamp(metarTime()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setStill(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (still) return undefined;
    const section = sectionRef.current;
    const el = {
      img: imgRef.current,
      panel: panelRef.current,
      content: contentRef.current,
      pill: pillRef.current,
      shade: shadeRef.current,
      fade: fadeRef.current,
    };
    let g;
    let raf = 0;
    let current = 0;

    const progress = () => {
      const total = section.offsetHeight - g.vh;
      return total > 0 ? clamp01(-section.getBoundingClientRect().top / total) : 0;
    };

    const layout = () => {
      g = measure(stageRef.current, hero);
      Object.assign(el.img.style, {
        width: `${g.W}px`,
        height: `${g.H}px`,
        left: `${g.L}px`,
        top: `${g.T}px`,
        transformOrigin: `${g.origin[0] - g.L}px ${g.origin[1] - g.T}px`,
      });
      Object.assign(el.panel.style, { width: `${g.panel.w}px`, height: `${g.panel.h}px` });
      setCompact(g.compact);
      current = progress();
      render(g, current, el);
    };

    // Ease toward the scroll position so wheel steps glide instead of jump.
    const frame = () => {
      raf = 0;
      const target = progress();
      current += (target - current) * 0.18;
      if (Math.abs(target - current) < 0.0005) current = target;
      render(g, current, el);
      if (current !== target) raf = requestAnimationFrame(frame);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };

    layout();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', layout);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', layout);
    };
  }, [hero, still]);

  const submit = (e) => {
    e.preventDefault();
    onStart(email);
  };

  return (
    <section
      ref={sectionRef}
      className={`hero${compact ? ' hero--compact' : ''}${still ? ' hero--still' : ''}${hero.screen ? '' : ' hero--float'}`}
      data-nav="over"
      aria-label="Introduction"
    >
      <div ref={stageRef} className="hero__stage">
        <img
          ref={imgRef}
          className="hero__img"
          src={imgUrl(hero.name, 1920)}
          srcSet={srcSet(hero.name, [1280, 1920, 2560, 3072])}
          sizes="(max-aspect-ratio: 16/9) 178vh, 100vw"
          style={{ objectPosition: `${hero.focus[0] * 100}% ${hero.focus[1] * 100}%` }}
          alt=""
          fetchpriority="high"
        />
        <div className="hero__tint" />
        <div className="hero__edge" />
        <div ref={shadeRef} className="hero__shade" />
        <div ref={fadeRef} className="hero__fade" />
        <div ref={panelRef} className="hero__panel" aria-hidden="true">
          <BriefingScreen compact={compact} />
        </div>
        <div className="hero__grain" />

        <div ref={contentRef} className="hero__content">
          <h1 className="h1 hero__title">{HERO.title}</h1>
          <p className="hero__lead">{HERO.lead}</p>
          <form className="hero__actions" onSubmit={submit}>
            <div className="hero__capture">
              <label htmlFor="hero-email" className="sr-only">
                Work email
              </label>
              <input
                id="hero-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Enter your work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" className="btn btn-primary">
                Start a project
              </button>
            </div>
            <a href="#capabilities" className="btn btn-glass">
              See what we build
            </a>
          </form>
        </div>

        <p ref={pillRef} className="hero__pill mono">
          <span className="hero__pill-dot" aria-hidden="true" />
          <span>
            METAR AVQT {stamp}
            <span className="hero__pill-wide"> 00000KT</span> CAVOK
            <span className="hero__pill-wide"> 21/09 Q1013</span> RMK OPEN FOR NEW PROJECTS
          </span>
        </p>
      </div>
    </section>
  );
}
